// --- Root DS
import { DEFAULT_VALUES } from '../../helpers/MvpBoardScoringHelper.js';

/**
 * This file is intended to have functions and variables that are useful for
 * the calculations of DS Value values on players. The DS Value calculations are
 * done in the WASM files, and sometimes we need to 'bridge' the data that the
 * app provides into what is required by the WASM files and their strict typing.
 */

/**
 * @type {object} The default values used to populate a PlayerData class in the
 * web assembly code. Since that is strictly typed all values must be present,
 * so merging this with player data before passing into the web assembly code
 * can reduce errors.
 */
const defaultPlayerDataValues = {
    sourcePlayerId: '',
    pick: 0,
    round: 0,
    hasStats: false,
    player_id: 0,
    nick_name: '',
    first_name: '',
    last_name: '',
    position: '',
    fantasy_position: '',
    experience: 0,
    average_adp_overall: 0,
    team_abbr: '',
    bye: 0,
    userAdjustmentPercent: 0,
    percent_low: 0,
    percent_high: 0,
    percent_high_odds: 0,
    extra: 0,
    extra_2: 0,
    flag_create_time: '',
    flag_order: 0,
    mvp_board_draft_pick_mvp_board_team_id: 0,
    overall_pick_number: 0,
    isMockDraftPick: false,
    isMockDraftGridLock: false,
    mvp_board_player_team_mvp_board_team_id: 0,
    is_user_owned: false,
    mvp_board_player_team_keeper: 0,
    tip_text: '',
    injury_prob: 0,
    positional_risk_group: 0,
    avgRemainingSos: 0,
    userComment: '',
    my_fantasy_league_id: '',
    yahoo_id: '',
    sleeper_id: '',
    nfl_id: '',
    flea_flicker_id: '',
    cbs_id: '',
    rt_sports_id: '',
    fantrax_id: '',
    espn_id: '',
    underdog_id: '',
    bgs_id: '',
    dsValueBase: 0,
    dsValue: 0,
    dsValueDraftBoard: 0,
    dsValueAltBase: 0,
    dsValueAlt: 0,
    dsValueAltDraftBoard: 0,
    rankOverallDsValue: 0,
    rankOverallDsValueDraftBoard: 0,
    rankFantasyPositionDsValue: 0,
    rankPositionDsValue: 0,
    rankOverallWeekly3dPts: 0,
    rankFantasyPositionWeekly3dPts: 0,
    rankPositionWeekly3dPts: 0,
    overallTier: 0,
    positionTier: 0,
    auction_market_factor: 1,
    auctionDollars: 0,
    auctionDollarsAlt: 0,
    auctionMarketValue: 0,
    winning_bid_amount: 0,
    threeYrPts: 0,
    fiveYrPts: 0,
    avgPts: 0,
    age: 0,
    estimatedGamesPlayed: 0,
    best_ball_factor: 100,
    bestBallStackBonusText: '',
    trust_factor: 1,
    nextPickOdds: 0,
    // --- For regular season tools
    rosPts: 0,
    rosCeilingPts: 0,
    rosFloorPts: 0,
    ros3dPts: 0,
    rosWeeklyPts: 0,
    rosWeeklyCeilingPts: 0,
    rosWeekly3dPts: 0,
    consensusRosPts: 0,
    consensusRosWeeklyPts: 0,
    weeklyPts: 0,
    weeklyFloorPts: 0,
    weeklyCeilingPts: 0,
    weeklyUpDown: '',
    consensusWeeklyPts: 0,
    weeklyPercentLow: 0,
    weeklyPercentHigh: 0,
    weekly3dPts: 0,
    weeklyComment: '',
    twoWeekPts: 0,
    tenYrPts: 0,
    team_id: 0,
    remainingSos: 0,
    hasInjuryReport: false,
    // --- For processing / calculations
    fantasyPtsNoReplacement: 0,
    replacementPts: 0,
    label: '',
    suggestedRosterSpot: '',
    slot_position: '',
    isLockedInSlotPosition: false,

    // --- FAF only so far
    headshot_id: '',
};

/**
 * Ensures that all properties on player data have at least a default value
 * @param {object} playerData Player data that we need to ensure default values
 * as required for the WASM code and its PlayerData type.
 * @return object
 */
export const assignMissingDefaultPlayerDataValues = function (playerData) {
    for (const defaultKey in defaultPlayerDataValues) {
        ensurePlayerDataPropertyHasValue(playerData, defaultKey);
    }
    return playerData;
};

/**
 * Ensures that a specific property on player data has at least a default value
 * @param {object} playerData Player data that we need to ensure has all of the
 * default values as required for the WASM code and its PlayerData type.
 * @param {string} property The property to ensure has a value
 */
export const ensurePlayerDataPropertyHasValue = function (playerData, property) {
    if (!playerData.hasOwnProperty(property) || playerData[property] === null) {
        playerData[property] = defaultPlayerDataValues[property];
    }
};